import React from 'react';
import { validCurrencyCode, makeMoneyFormatter, getDisplayCurrency, convertAmount } from '../../utils/currency';

/**
 * Renders a money amount in the currency it was recorded in, plus an
 * equivalent in another currency, e.g.
 *
 *     $440.00
 *     = AFN 28,380.00
 *
 * Props:
 *   amount/currency  - the figure and the currency it is denominated in
 *   baseCurrency     - the company base currency (from useCompany)
 *   convertTo        - show the equivalent in this currency instead of the
 *                      base currency (used for base-denominated balances shown
 *                      against a party that trades in another currency)
 *   rate             - base units per 1 unit of `currency`; omit to derive it
 *                      from exchangeRate/baseAmount
 *   exchangeRate     - per-record rate (payments, invoices, bills)
 *   baseAmount       - stored base-currency equivalent, when available
 */
export default function FxAmount({
  amount,
  currency,
  baseCurrency,
  convertTo,
  rate,
  exchangeRate,
  baseAmount,
  showRate = false,
  className = '',
  style,
}) {
  const base = validCurrencyCode(baseCurrency) ? String(baseCurrency).toUpperCase() : getDisplayCurrency();
  const src = validCurrencyCode(currency) ? String(currency).toUpperCase() : base;
  const n = amount === null || amount === undefined || isNaN(Number(amount)) ? 0 : Number(amount);

  // Where the second figure goes: an explicit convertTo wins, otherwise the
  // base currency whenever the source is foreign.
  const target = validCurrencyCode(convertTo)
    ? String(convertTo).toUpperCase()
    : (src !== base ? base : null);

  let r = Number(rate);
  if (!r || isNaN(r) || r <= 0) {
    const own = Number(exchangeRate);
    if (own > 0 && src !== base) r = own;
    else if (baseAmount !== null && baseAmount !== undefined && baseAmount !== '' && n > 0 && src !== base) {
      r = Number(baseAmount) / n;
    } else r = 1;
  }

  // A rate is always expressed as base units per 1 source unit; flip it when
  // the equivalent is needed in a non-base target currency.
  const effectiveRate = target && target !== base ? 1 / r : r;

  const converted = target === base && baseAmount !== null && baseAmount !== undefined && baseAmount !== ''
    ? Number(baseAmount)
    : convertAmount(n, effectiveRate);

  const fmtSrc = makeMoneyFormatter(src);
  const fmtTarget = makeMoneyFormatter(target || base);

  return (
    <div className={className} style={style}>
      <div>{fmtSrc(n)}</div>
      {target && target !== src && (
        <div style={{ fontSize: 11, color: '#0f766e', fontWeight: 600, whiteSpace: 'nowrap' }}>
          = {fmtTarget(converted)}
        </div>
      )}
      {showRate && src !== base && (
        <div style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          1 {src} = {r.toLocaleString('en-US', { maximumFractionDigits: 4 })} {base}
        </div>
      )}
    </div>
  );
}
